export function getEntitiesMissingAnyKeys(dataSource, keyNames = []) {
  if (!dataSource || !dataSource.entities || !Array.isArray(keyNames) || keyNames.length === 0) {
    return [];
  }

  const requiredKeys = keyNames.map((k) => String(k).trim()).filter(Boolean);
  if (requiredKeys.length === 0) return [];

  const entities = dataSource.entities.values || dataSource.entities;
  if (!Array.isArray(entities) || entities.length === 0) return [];

  function getEntityId(entity) {
    if (!entity) return undefined;
    const props = entity.properties;

    let idFromProps;

    if (props) {
      if (typeof props.get === "function") {
        const property = props.get("id");
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && props._properties && typeof props._properties === "object") {
        const property = props._properties.id;
        if (property) idFromProps = typeof property.getValue === "function" ? property.getValue() : property;
      }

      if (idFromProps === undefined && typeof props.getValue === "function") {
        const obj = props.getValue();
        if (obj && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "id")) {
          idFromProps = obj.id;
        }
      }

      if (idFromProps === undefined && Object.prototype.hasOwnProperty.call(props, "id")) {
        idFromProps = props.id;
      }
    }

    if (idFromProps !== undefined && idFromProps !== null) return idFromProps;
    return entity.id;
  }

  function buildReasons(missingKeys) {
    return missingKeys.map((k) => `Missing key: ${k}`);
  }

  const result = [];
  const seenIds = new Set();

  for (const entity of entities) {
    if (!entity) continue;

    const props = entity.properties;

    // No properties -> missing all required keys
    if (!props) {
      const id = getEntityId(entity);
      const dedupeKey = id ?? entity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({
          entity,
          id,
          reasons: buildReasons(requiredKeys),
        });
      }
      continue;
    }

    // Extract property keys
    let propKeys = null;

    if (Array.isArray(props.propertyNames)) {
      propKeys = props.propertyNames;
    } else if (typeof props.getValue === "function") {
      const valueObj = props.getValue();
      if (valueObj && typeof valueObj === "object") propKeys = Object.keys(valueObj);
    } else if (props._properties && typeof props._properties === "object") {
      propKeys = Object.keys(props._properties);
    } else if (typeof props === "object") {
      propKeys = Object.keys(props);
    }

    // Cannot read keys -> treat as missing all required keys
    if (!propKeys) {
      const id = getEntityId(entity);
      const dedupeKey = id ?? entity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({
          entity,
          id,
          reasons: buildReasons(requiredKeys),
        });
      }
      continue;
    }

    const keySet = new Set(propKeys);

    const missingKeys = [];
    for (const key of requiredKeys) {
      if (!keySet.has(key)) missingKeys.push(key);
    }

    if (missingKeys.length > 0) {
      const id = getEntityId(entity);
      const dedupeKey = id ?? entity;

      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        result.push({
          entity,
          id,
          reasons: buildReasons(missingKeys),
        });
      }
    }
  }

  return result;
}
